import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTheme } from '@/context/ThemeProvider';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { X } from 'lucide-react';

import { fetchResponsivenessData } from '@/hooks/report';

import { DateRangePicker } from '../common/date-range-picker/data-range-picker';
import { Button } from '../ui/button';
import { ScrollArea } from '../ui/scroll-area';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';

// Utility function to format milliseconds to human-readable format
const formatTime = (ms) => {
  if (ms === 0) return '0s';

  const seconds = Math.floor(ms / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);

  if (days > 0) {
    const remainingHours = hours % 24;
    if (remainingHours > 0) {
      return `${days}d ${remainingHours}h`;
    }
    return `${days}d`;
  }

  if (hours > 0) {
    const remainingMinutes = minutes % 60;
    return `${hours}h ${remainingMinutes}m`;
  }

  if (minutes > 0) {
    const remainingSeconds = seconds % 60;
    return `${minutes}m ${remainingSeconds}s`;
  }

  return `${seconds}s`;
};

// Reusable chart options function
const createSplineChartOptions = (
  title,
  subtitle,
  yAxisTitle,
  dates,
  data,
  tooltipLabel,
  isDarkTheme
) => {
  return {
    chart: {
      type: 'spline',
      backgroundColor: 'transparent',
      height: 300,
    },
    title: {
      text: title,
      style: {
        color: isDarkTheme ? '#fff' : '#000',
        fontFamily: 'Inter, sans-serif',
      },
    },
    ...(subtitle && {
      subtitle: {
        text: subtitle,
        style: {
          color: isDarkTheme ? '#999' : '#666',
          fontFamily: 'Inter, sans-serif',
        },
      },
    }),
    credits: {
      enabled: false,
    },
    xAxis: {
      categories: dates,
      labels: {
        style: {
          color: isDarkTheme ? '#fff' : '#000',
        },
      },
    },
    yAxis: {
      title: {
        text: yAxisTitle,
        style: {
          color: isDarkTheme ? '#fff' : '#000',
        },
      },
      labels: {
        formatter: function () {
          return formatTime(this.value);
        },
        style: {
          color: isDarkTheme ? '#fff' : '#000',
        },
      },
      gridLineWidth: 0,
    },
    tooltip: {
      useHTML: true,
      formatter: function () {
        const dateValue = this.point.category || this.x;
        return `
          <div style="text-align: center;">
            <div style="font-weight: 600; margin-bottom: 4px;">${dateValue}</div>
            <div>${formatTime(this.y)}</div>
          </div>
        `;
      },
    },
    series: [
      {
        name: title,
        data: data,
        color: '#3b82f6',
      },
    ],
    plotOptions: {
      spline: {
        marker: {
          enabled: true,
          radius: 4,
        },
      },
    },
  };
};

const Responses = () => {
  const [loading, setLoading] = useState(true);
  const [type, setType] = useState('pre-defined');
  const [value, setValue] = useState('month-to-date');
  const [selectedAgentId, setSelectedAgentId] = useState(null);
  const [data, setData] = useState(null);
  const { currentTheme } = useTheme();
  const isDarkTheme = currentTheme === 'dark';

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const result = await fetchResponsivenessData(
        type,
        value,
        selectedAgentId
      );
      setData(result);
    } catch (error) {
      console.error('Error fetching responsiveness data:', error);
    } finally {
      setLoading(false);
    }
  }, [type, value, selectedAgentId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const updateDateFilter = (values) => {
    const { range, selectedPreset } = values;

    if (selectedPreset === 'custom') {
      const formatDate = (date) => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
      };

      setValue([formatDate(range.from), formatDate(range.to)]);
      setType('custom');
    } else {
      setValue(selectedPreset);
      setType('pre-defined');
    }
  };

  const handleAgentClick = (agentId) => {
    if (selectedAgentId === agentId) {
      setSelectedAgentId(null);
    } else {
      setSelectedAgentId(agentId);
    }
  };

  const selectedMember = data?.performance_chart?.find(
    (member) => member.user_id === selectedAgentId
  );

  // Response Time Chart Options
  const responseTimeChartOptions = useMemo(() => {
    if (!data?.response_time_chart) return null;

    const dates = data.response_time_chart.dates || [];
    const responseTimes = data.response_time_chart.response_time || [];

    return createSplineChartOptions(
      'Response Time',
      'Median response time',
      'Response Time (ms)',
      dates,
      responseTimes,
      'Response Time',
      isDarkTheme
    );
  }, [data, isDarkTheme]);

  // Speed to Lead Chart Options
  const speedToLeadChartOptions = useMemo(() => {
    if (!data?.speed_to_lead_chart) return null;

    const dates = data.speed_to_lead_chart.dates || [];
    const responseTimes = data.speed_to_lead_chart.response_time || [];

    return createSplineChartOptions(
      'Speed to Lead Over Time',
      'Median time to first reply on new leads',
      'Speed to Lead (ms)',
      dates,
      responseTimes,
      'Speed to Lead',
      isDarkTheme
    );
  }, [data, isDarkTheme]);

  return (
    <ScrollArea className='h-full'>
      <div className='flex flex-col space-y-4 p-4 pb-8'>
        {/* Selected Team Member Banner */}
        {selectedMember && (
          <div className='flex items-center justify-between rounded-lg border bg-muted/50 p-3'>
            <div className='flex items-center gap-2'>
              <span className='text-sm text-muted-foreground'>
                Showing data for:
              </span>
              <span className='font-semibold'>{selectedMember.name}</span>
            </div>
            <Button
              variant='ghost'
              size='sm'
              onClick={() => setSelectedAgentId(null)}
              className='h-8 w-8 p-0'
            >
              <X className='h-4 w-4' />
            </Button>
          </div>
        )}

        <div className='flex items-center justify-between'>
          <div className='text-2xl font-semibold'>Responses</div>
          <DateRangePicker
            defaultPreset={value}
            onUpdate={(values) => updateDateFilter(values)}
            align='end'
            locale='en-US'
            showCompare={false}
          />
        </div>

        {/* Metric Cards */}
        <div className='grid grid-cols-1 gap-4 md:grid-cols-2'>
          <div className='rounded-lg border bg-card p-6'>
            <div className='text-sm font-medium text-muted-foreground'>
              Median Speed to Lead
            </div>
            <div className='mt-2 text-3xl font-bold'>
              {loading
                ? 'Loading...'
                : data?.median_speed_to_lead !== undefined
                  ? formatTime(data.median_speed_to_lead)
                  : 'N/A'}
            </div>
          </div>
          <div className='rounded-lg border bg-card p-6'>
            <div className='text-sm font-medium text-muted-foreground'>
              Median Response Time
            </div>
            <div className='mt-2 text-3xl font-bold'>
              {loading
                ? 'Loading...'
                : data?.median_response_time !== undefined
                  ? formatTime(data.median_response_time)
                  : 'N/A'}
            </div>
          </div>
        </div>

        {/* Charts */}
        <div className='flex flex-col gap-4'>
          {loading ? (
            <div className='flex h-[300px] items-center justify-center rounded-lg border'>
              <div className='animate-pulse'>Loading...</div>
            </div>
          ) : responseTimeChartOptions ? (
            <div className='rounded-lg border bg-card p-4'>
              <HighchartsReact
                highcharts={Highcharts}
                options={responseTimeChartOptions}
              />
            </div>
          ) : (
            <div className='flex h-[300px] items-center justify-center rounded-lg border bg-card'>
              <div className='text-muted-foreground'>
                No response time data available
              </div>
            </div>
          )}

          {loading ? (
            <div className='flex h-[300px] items-center justify-center rounded-lg border'>
              <div className='animate-pulse'>Loading...</div>
            </div>
          ) : speedToLeadChartOptions ? (
            <div className='rounded-lg border bg-card p-4'>
              <HighchartsReact
                highcharts={Highcharts}
                options={speedToLeadChartOptions}
              />
            </div>
          ) : (
            <div className='flex h-[300px] items-center justify-center rounded-lg border bg-card'>
              <div className='text-muted-foreground'>
                No speed to lead data available
              </div>
            </div>
          )}
        </div>

        {/* Team Performance Table */}
        <div className='rounded-lg border bg-card'>
          <div className='p-4'>
            <h3 className='text-lg font-semibold'>Team Performance</h3>
          </div>
          {loading ? (
            <div className='flex h-[200px] items-center justify-center'>
              <div className='animate-pulse'>Loading...</div>
            </div>
          ) : data?.performance_chart && data.performance_chart.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Member</TableHead>
                  <TableHead>Median Speed to Lead</TableHead>
                  <TableHead>Avg Response Time</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.performance_chart.map((member) => (
                  <TableRow
                    key={member.user_id}
                    onClick={() => handleAgentClick(member.user_id)}
                    className={`cursor-pointer ${
                      selectedAgentId === member.user_id
                        ? 'bg-muted'
                        : 'hover:bg-muted/50'
                    }`}
                  >
                    <TableCell className='font-medium'>{member.name}</TableCell>
                    <TableCell>
                      {formatTime(member.speed_to_lead || 0)}
                    </TableCell>
                    <TableCell>
                      {formatTime(member.median_response_time || 0)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <div className='flex h-[200px] items-center justify-center'>
              <div className='text-muted-foreground'>
                No member data available
              </div>
            </div>
          )}
        </div>
      </div>
    </ScrollArea>
  );
};

export default Responses;
